import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { RAG } from "@busright/ui";

import { formatScore } from "@/lib/format";

export interface BulletGaugeProps {
  readonly label: string;
  readonly value: number | null;
  // Max of the scale (subscores are 0–100).
  readonly max?: number;
  // Threshold marker (e.g. the WARN/CRIT cut line). Drawn as a vertical tick.
  readonly threshold?: number;
  readonly icon?: LucideIcon;
}

// A horizontal bullet bar with a threshold marker. Fill is RAG-colored by how the value sits vs
// the threshold (this is a health subscore, so RAG is appropriate).
const WARN_BAND = 10;

const fillColor = (value: number, threshold: number | undefined): string => {
  if (threshold === undefined) {
    return RAG.OK;
  }
  if (value < threshold) {
    return RAG.CRITICAL;
  }
  if (value < threshold + WARN_BAND) {
    return RAG.WARN;
  }
  return RAG.OK;
};

export const BulletGauge = ({
  label,
  value,
  max = 100,
  threshold,
  icon: Icon,
}: BulletGaugeProps): ReactNode => {
  const pct = value === null ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  const color = value === null ? RAG.WARN : fillColor(value, threshold);
  return (
    <div className="bullet">
      <span className="bullet-label">
        {Icon ? <Icon size={14} aria-hidden /> : null}
        {label}
      </span>
      <div className="bullet-track" title={threshold ? `threshold ${threshold}` : undefined}>
        <div className="bullet-fill" style={{ width: `${pct}%`, background: color }} />
        {threshold !== undefined ? (
          <div className="bullet-threshold" style={{ left: `${(threshold / max) * 100}%` }} />
        ) : null}
      </div>
      <span className="bullet-value br-tabular-nums">{formatScore(value)}</span>
    </div>
  );
};
